import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Building2, RefreshCw, Search, Shield, UserCheck, Users, UserX } from 'lucide-react';

import { getRequestAuthToken } from '../../auth/appliedAuthToken';
import { useAuth } from '../../contexts/AuthContext';

interface AdminUserSummary {
  username: string;
  email: string | null;
  org_id: string | null;
  role: string | null;
  cognito_status: string | null;
  enabled: boolean;
}

interface AdminUserPage {
  users: AdminUserSummary[];
  returned: number;
  page_size: number;
  next_cursor: string | null;
}

type LoadState = 'loading' | 'ready' | 'unavailable';

const AdminUsersAws: React.FC = () => {
  const { t } = useTranslation(['admin_users_aws', 'admin_users', 'admin_common', 'common']);
  const { user } = useAuth();
  const [queryInput, setQueryInput] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([null]);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [reloadEpoch, setReloadEpoch] = useState(0);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [data, setData] = useState<AdminUserPage | null>(null);

  const currentCursor = cursorHistory[cursorIndex] || null;

  useEffect(() => {
    if (user?.role !== 'superadmin') return;
    const token = getRequestAuthToken();
    if (!token) {
      setData(null);
      setLoadState('unavailable');
      return;
    }

    let mounted = true;
    setLoadState('loading');
    const params = new URLSearchParams({ page_size: '20' });
    if (appliedQuery) params.set('q', appliedQuery);
    if (currentCursor) params.set('cursor', currentCursor);

    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/users?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('admin_users_unavailable');
        return response.json() as Promise<AdminUserPage>;
      })
      .then((payload) => {
        if (!mounted) return;
        if (
          !Array.isArray(payload.users)
          || typeof payload.returned !== 'number'
          || typeof payload.page_size !== 'number'
          || !(payload.next_cursor === null || typeof payload.next_cursor === 'string')
        ) {
          throw new Error('admin_users_invalid');
        }
        setData(payload);
        setLoadState('ready');
      })
      .catch(() => {
        if (!mounted) return;
        setData(null);
        setLoadState('unavailable');
      });

    return () => { mounted = false; };
  }, [user?.role, appliedQuery, currentCursor, reloadEpoch]);

  if (user?.role !== 'superadmin') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="admin-users-denied">
        <div className="text-center">
          <Shield className="mx-auto mb-4 h-14 w-14 text-red-500" />
          <h1 className="text-2xl font-bold text-gray-900">{t('admin_common:accessDenied')}</h1>
          <p className="mt-2 text-gray-600">{t('admin_common:superadminRequired')}</p>
        </div>
      </div>
    );
  }

  const roleLabel = (role: string | null): string => {
    switch ((role || '').toLowerCase()) {
      case 'superadmin': return t('admin_users:roles.superadmin');
      case 'coop_admin': return t('admin_users:roles.coop_admin');
      case 'farmer': return t('admin_users:roles.farmer');
      case 'technician': return t('admin_users:roles.technician');
      case 'farm_manager': return t('admin_users_aws:roles.farmManager');
      case 'viewer': return t('admin_users_aws:roles.viewer');
      case 'org_admin': return t('admin_users_aws:roles.orgAdmin');
      case 'owner': return t('admin_users_aws:roles.owner');
      case 'admin': return t('admin_users_aws:roles.admin');
      default: return role || '—';
    }
  };

  const statusLabel = (status: string | null): string => {
    switch ((status || '').toUpperCase()) {
      case 'CONFIRMED': return t('admin_users_aws:status.confirmed');
      case 'UNCONFIRMED': return t('admin_users_aws:status.unconfirmed');
      case 'FORCE_CHANGE_PASSWORD': return t('admin_users_aws:status.forceChangePassword');
      case 'RESET_REQUIRED': return t('admin_users_aws:status.resetRequired');
      case 'ARCHIVED': return t('admin_users_aws:status.archived');
      case 'COMPROMISED': return t('admin_users_aws:status.compromised');
      case 'EXTERNAL_PROVIDER': return t('admin_users_aws:status.externalProvider');
      case 'UNKNOWN': return t('admin_users_aws:status.unknown');
      default: return status || '—';
    }
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setCursorHistory([null]);
    setCursorIndex(0);
    setAppliedQuery(queryInput.trim().slice(0, 100));
  };

  const goNext = () => {
    if (!data?.next_cursor) return;
    const nextIndex = cursorIndex + 1;
    setCursorHistory((history) => [...history.slice(0, nextIndex), data.next_cursor]);
    setCursorIndex(nextIndex);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6" data-testid="admin-users-page">
      <section>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin_users:title')}</h1>
        <p className="mt-1 text-sm text-gray-600">{t('admin_users_aws:subtitle')}</p>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
        <form onSubmit={submitSearch} className="flex flex-col gap-3 sm:flex-row">
          <label className="relative flex-1">
            <span className="sr-only">{t('admin_users_aws:searchLabel')}</span>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={queryInput}
              onChange={(event) => setQueryInput(event.target.value)}
              maxLength={100}
              placeholder={t('admin_users_aws:searchPlaceholder')}
              className="min-h-[44px] w-full rounded-xl border border-gray-200 pl-10 pr-3 text-sm"
              data-testid="admin-users-search"
            />
          </label>
          <button type="submit" className="min-h-[44px] rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700">
            {t('common:search')}
          </button>
        </form>
        <p className="mt-3 text-xs text-gray-500">{t('admin_users_aws:searchHint')}</p>
      </section>

      {loadState === 'loading' && (
        <section className="flex min-h-[220px] items-center justify-center rounded-2xl border border-gray-200 bg-white" data-testid="admin-users-loading">
          <RefreshCw className="h-7 w-7 animate-spin text-gray-400" />
        </section>
      )}

      {loadState === 'unavailable' && (
        <section className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800" data-testid="admin-users-unavailable">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5" />
            <div>
              <h2 className="font-semibold">{t('admin_users_aws:unavailableTitle')}</h2>
              <p className="mt-1 text-sm">{t('admin_users_aws:unavailableMessage')}</p>
              <button type="button" onClick={() => setReloadEpoch((value) => value + 1)} className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold">
                {t('common:refresh')}
              </button>
            </div>
          </div>
        </section>
      )}

      {loadState === 'ready' && data && (
        <>
          <section>
            <p className="text-sm font-semibold text-gray-900">{t('admin_users_aws:pageCount', { count: data.returned })}</p>
            <p className="text-xs text-gray-500">{t('admin_users_aws:noGlobalTotal')}</p>
          </section>

          {data.users.length === 0 ? (
            <section className="rounded-2xl border border-gray-200 bg-white p-10 text-center" data-testid="admin-users-empty">
              <h2 className="font-semibold text-gray-800">{t('admin_users_aws:emptyTitle')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('admin_users_aws:emptyMessage')}</p>
            </section>
          ) : (
            <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-left">
                  <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
                    <tr>
                      <th className="px-5 py-3">{t('admin_users:table.user')}</th>
                      <th className="px-5 py-3">{t('common:organization')}</th>
                      <th className="px-5 py-3">{t('admin_users:table.role')}</th>
                      <th className="px-5 py-3">{t('admin_users:table.status')}</th>
                      <th className="px-5 py-3">{t('admin_users_aws:access')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.users.map((entry) => (
                      <tr key={entry.username} data-testid={`admin-user-${entry.username}`}>
                        <td className="px-5 py-4 text-sm font-semibold text-gray-900">{entry.email || entry.username}</td>
                        <td className="px-5 py-4 text-sm text-gray-700"><span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-gray-400" />{entry.org_id || '—'}</span></td>
                        <td className="px-5 py-4 text-sm text-gray-700">{roleLabel(entry.role)}</td>
                        <td className="px-5 py-4 text-sm text-gray-700">{statusLabel(entry.cognito_status)}</td>
                        <td className="px-5 py-4 text-sm">
                          {entry.enabled
                            ? <span className="inline-flex items-center gap-1 text-emerald-700"><UserCheck className="h-4 w-4" />{t('common:enabled')}</span>
                            : <span className="inline-flex items-center gap-1 text-red-700"><UserX className="h-4 w-4" />{t('common:disabled')}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <nav className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3" aria-label={t('admin_users_aws:paginationLabel')}>
            <button type="button" onClick={() => setCursorIndex((value) => Math.max(0, value - 1))} disabled={cursorIndex === 0} className="rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-40">
              {t('common:previous')}
            </button>
            <span className="text-sm text-gray-500">{t('common:page')} {cursorIndex + 1}</span>
            <button type="button" onClick={goNext} disabled={!data.next_cursor} className="rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-40">
              {t('common:next')}
            </button>
          </nav>
        </>
      )}
    </div>
  );
};

export default AdminUsersAws;